import { useCallback, useState } from "react";

import useEnergy from "../../../shared/hooks/useEnergy";
import { useContentCompletion } from "./useContentCompletion";

const ENERGY_PER_MISTAKE = 1;

// consumeEnergy() errTypes that mean "request problem", not "not enough energy"
const TECHNICAL_ERR_TYPES = ["server", "network"];

interface UseLessonSessionParams {
    userId?: number | string;
    courseId: string;
    contentId: string;
    lessonAttemptId?: number | string | null;
    alreadyCompleted: boolean;
}

// A lesson's session: the shared completion tracking, plus energy for mistakes (first completions only)
export const useLessonSession = ({
                                     userId,
                                     courseId,
                                     contentId,
                                     lessonAttemptId,
                                     alreadyCompleted
                                 }: UseLessonSessionParams) => {
    const { consumeEnergy } = useEnergy(userId);
    const completion = useContentCompletion({
        userId,
        courseId,
        contentId,
        attemptId: lessonAttemptId,
        alreadyCompleted
    });
    const { recordAnswer: countAnswer } = completion;

    const [outOfEnergy, setOutOfEnergy] = useState(false);

    // Called by question blocks with true/false for every answer
    const recordAnswer = useCallback(
        async (isCorrect: boolean): Promise<void> => {
            countAnswer(isCorrect);

            // Replaying a finished lesson is free
            if (isCorrect || alreadyCompleted) return;

            const response = await consumeEnergy(ENERGY_PER_MISTAKE);

            if (!response.success && response.errType && !TECHNICAL_ERR_TYPES.includes(response.errType)) {
                setOutOfEnergy(true);
            }
        },
        [alreadyCompleted, consumeEnergy, countAnswer]
    );

    return {
        recordAnswer,
        recordHint: completion.recordHint,
        completeLesson: completion.complete,
        completing: completion.completing,
        completeError: completion.error,
        outOfEnergy,
        result: completion.result
    };
};